import { isEmpty } from 'lodash';
import { type ReactElement, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import MainLayout from 'layouts/MainLayout';
import { ADMIN_ROUTES, CLIENT_ROUTES } from 'routes/main';

import { getValueFromStorage, setValueToStorage } from 'hooks/useLocalStorage';
import { useAppSelector } from 'hooks/useRedux';
import { jwtTokenSelector, loggedUserSelector, userRoleSelector } from 'store/user/selectors';

import { NEWS_ROUTE, SIGNIN_ROUTE } from 'constants/clientRoutes';
import { LocalStorageKeys } from 'constants/enums';
import { isAdmin } from 'utils/permissions';
import { isRouteAllowed } from 'utils/routes';

const ProtectedRoute = (): ReactElement | null => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(loggedUserSelector);
  const jwtToken = useAppSelector(jwtTokenSelector);
  const storageUser = getValueFromStorage(LocalStorageKeys.user, null) || {};
  const storageJwtToken = getValueFromStorage(LocalStorageKeys.jwtToken, null) || {};
  const userRole = useAppSelector(userRoleSelector);
  const routes = userRole && isAdmin(userRole) ? ADMIN_ROUTES : CLIENT_ROUTES;

  useEffect(() => {
    if ((!jwtToken && isEmpty(storageJwtToken)) || (!user && isEmpty(storageUser))) {
      navigate(SIGNIN_ROUTE);
      setValueToStorage(LocalStorageKeys.jwtToken, null);
      setValueToStorage(LocalStorageKeys.user, null);
    }
  }, [user, storageUser, jwtToken, storageJwtToken]);

  useEffect(() => {
    const isAllowed = isRouteAllowed(routes, location?.pathname);

    if (routes?.length && userRole && !isAllowed && location?.pathname) {
      navigate(NEWS_ROUTE);
    }
  }, [routes, userRole, location?.pathname]);

  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
};

export default ProtectedRoute;
